import React, { useEffect, useState } from 'react';
import { Sparkles, DollarSign, Lock, CheckCircle2, XCircle, Loader2, Eye } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { usePlatformSettings } from '../../contexts/PlatformSettingsContext';
import AdminVisibilityRadiusCard from './AdminVisibilityRadiusCard';

/**
 * AdminPlatformTogglesPanel — super-admin controls for platform-wide
 * feature flags. Introduced Feb 25, 2026 (iter173).
 *
 * Currently exposes:
 *   • pay_rate_editing_enabled — when off, every shift/job creation
 *     form platform-wide renders the hourly-rate input as read-only
 *     and the backend coerces incoming rates to 0.
 */
export default function AdminPlatformTogglesPanel({ embedded = false }) {
  const { payRateEditingEnabled, lockedRateDisplay, refresh } = usePlatformSettings();
  const [busy, setBusy] = useState(false);
  const [localEnabled, setLocalEnabled] = useState(payRateEditingEnabled);
  const [localDisplay, setLocalDisplay] = useState(lockedRateDisplay || 'agency');
  const [displayBusy, setDisplayBusy] = useState(false);

  useEffect(() => setLocalEnabled(payRateEditingEnabled), [payRateEditingEnabled]);
  useEffect(() => setLocalDisplay(lockedRateDisplay || 'agency'), [lockedRateDisplay]);

  const setDisplay = async (display) => {
    if (display === localDisplay) return;
    setDisplayBusy(true);
    try {
      await apiCall('/api/admin/platform-settings/locked-rate-display', { method: 'PATCH', body: JSON.stringify({ display }) });
      setLocalDisplay(display);
      await refresh();
      toast.success(display === 'hidden' ? 'Workers will not see pay on locked-rate shifts' : 'Workers will see "Rate set by agency" on locked-rate shifts');
    } catch (e) {
      toast.error(e?.message || 'Update failed');
    }
    setDisplayBusy(false);
  };

  const toggle = async () => {
    const next = !localEnabled;
    setBusy(true);
    try {
      await apiCall('/api/admin/platform-settings/pay-rate-editing', {
        method: 'PATCH',
        body: JSON.stringify({ enabled: next }),
      });
      setLocalEnabled(next);
      await refresh(); // force cross-tab context refresh
      toast.success(
        next
          ? 'Pay-rate editing enabled platform-wide'
          : 'Pay-rate editing disabled platform-wide',
      );
    } catch (e) {
      toast.error(e?.message || 'Toggle failed');
    }
    setBusy(false);
  };

  return (
    <div className={embedded ? 'space-y-3' : 'space-y-6'} data-testid="admin-platform-toggles-panel">
      {embedded ? (
        <p className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-indigo-500" /> Platform Toggles</p>
      ) : (
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-indigo-600" />
          Platform Toggles
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Global feature switches. Changes propagate to every employer,
          agency, program manager, and group-home shift creation window
          within one minute.
        </p>
      </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Shift Pay-Rate Editing
          </h3>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
              localEnabled
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-slate-200 text-slate-700'
            }`}
            data-testid="platform-toggle-pay-rate-status"
          >
            {localEnabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            {localEnabled ? 'ENABLED' : 'DISABLED'}
          </span>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            When <b>disabled</b>, the hourly-rate input on every
            shift-creation window (employer basic form, "Post Job with AI",
            program manager, group-home shift, agent portal) is rendered
            as read-only with a "Locked by admin" badge. The backend
            simultaneously coerces any incoming pay_rate to <code>$0</code>{' '}
            so bypassing the UI has no effect.
          </p>

          <p className="text-sm text-slate-600 leading-relaxed">
            When <b>enabled</b>, all shift creation windows regain the
            editable pay-rate field and jobs post with the value the
            employer or agent enters — nothing else on the page changes.
          </p>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div>
              <p className="font-semibold text-slate-900">
                Allow employers &amp; agents to set their own hourly rate
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Toggle affects every shift creation surface across the platform in real time.
              </p>
            </div>
            <button
              onClick={toggle}
              disabled={busy}
              type="button"
              role="switch"
              aria-checked={localEnabled}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 ${
                localEnabled ? 'bg-emerald-600' : 'bg-slate-300'
              }`}
              data-testid="platform-toggle-pay-rate-switch"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  localEnabled ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
              {busy && (
                <Loader2 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-white" />
              )}
            </button>
          </div>

          {!localEnabled && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 space-y-3" data-testid="locked-rate-display-card">
              <div>
                <p className="font-semibold text-slate-900 flex items-center gap-2"><Eye className="w-4 h-4 text-indigo-600" /> What workers see on locked-rate shifts</p>
                <p className="text-xs text-slate-500 mt-0.5">Applies to every shift posted without a rate while editing is locked — job cards, My Shifts, shift details and the check-out summary.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  { id: 'agency', title: 'Show "Rate set by agency"', desc: 'Workers see a neutral label instead of an amount.' },
                  { id: 'hidden', title: 'Hide pay entirely', desc: 'No rate, total or estimated-pay fields are shown.' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDisplay(opt.id)}
                    disabled={displayBusy}
                    className={`text-left p-3 rounded-lg border transition ${
                      localDisplay === opt.id ? 'border-indigo-500 bg-white ring-2 ring-indigo-200' : 'border-slate-200 bg-white/70 hover:bg-white'
                    } disabled:opacity-60`}
                    data-testid={`locked-rate-display-${opt.id}`}
                    aria-pressed={localDisplay === opt.id}
                  >
                    <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      {localDisplay === opt.id && <CheckCircle2 className="w-4 h-4 text-indigo-600" />}
                      {opt.title}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!localEnabled && (
            <div className="rounded-lg bg-slate-900 text-slate-100 text-xs p-3 flex items-start gap-2">
              <Lock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-300" />
              <div>
                <p className="font-semibold">Currently disabled platform-wide</p>
                <p className="opacity-80 mt-0.5">
                  Every open shift-creation form on any other tab will lock its
                  pay-rate input within ~60 seconds; new sessions see it locked
                  immediately.
                </p>
              </div>
            </div>
          )}

          {localEnabled && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs p-3 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Currently enabled platform-wide</p>
                <p className="opacity-80 mt-0.5">
                  Employers and agents can set their own hourly rates on any shift.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <AdminVisibilityRadiusCard />
    </div>
  );
}

 
const _RESERVED_ICON_USE = XCircle;
